import React, { useState, useEffect, useCallback } from 'react';
import { governanceAPI } from '../../../lib/governanceApi';
import { toast } from 'sonner';
import {
  DollarSign, TrendingUp, Wallet, PiggyBank, Percent, Megaphone,
  PhoneOff, Users, RefreshCw, Activity,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import { PageHeader, MetricRow, DataTable } from '../design';

const fmtUSD = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtUSD4 = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;

const PLAN_TONE = {
  free: 'bg-slate-500/15 text-slate-300',
  starter: 'bg-sky-500/15 text-sky-300',
  growth: 'bg-violet-500/15 text-violet-300',
  scale: 'bg-amber-500/15 text-amber-300',
  enterprise: 'bg-emerald-500/15 text-emerald-300',
};

const marginTone = (pct) => (pct >= 70 ? 'text-emerald-400' : pct >= 40 ? 'text-amber-400' : 'text-rose-400');

export default function Economics() {
  const [days, setDays] = useState('30');
  const [overview, setOverview] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, tn, tl] = await Promise.allSettled([
        governanceAPI.getEconomicsOverview(days),
        governanceAPI.getEconomicsTenants(days),
        governanceAPI.getToolMargins(days),
      ]);
      if (ov.status === 'fulfilled') setOverview(ov.value.data);
      if (tn.status === 'fulfilled') setTenants(tn.value.data.tenants || []);
      if (tl.status === 'fulfilled') setTools(tl.value.data.actions || []);
      if (ov.status === 'rejected') toast.error('Failed to load economics overview');
    } catch (e) {
      toast.error('Failed to load economics');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const o = overview || {};
  const heroMetrics = [
    { key: 'mrr', label: 'MRR', value: fmtUSD(o.mrr_usd), icon: DollarSign, tone: 'success', hint: `${o.paying_tenants || 0} paying tenants` },
    { key: 'rev', label: `Revenue (${o.window_days || days}d)`, value: fmtUSD(o.total_revenue_usd), icon: TrendingUp, hint: `Usage ${fmtUSD(o.usage_revenue_usd)}` },
    { key: 'cogs', label: 'COGS', value: fmtUSD4(o.cogs_total_usd), icon: Wallet, tone: 'warning', hint: `Measured ${fmtUSD4(o.cogs_measured_usd)} · Modeled ${fmtUSD4(o.cogs_modeled_usd)}` },
    { key: 'profit', label: 'Gross Profit', value: fmtUSD(o.gross_profit_usd), icon: PiggyBank, tone: 'success' },
    { key: 'margin', label: 'Gross Margin', value: `${o.gross_margin_pct || 0}%`, icon: Percent, tone: (o.gross_margin_pct || 0) >= 70 ? 'success' : 'warning' },
  ];
  const secondaryMetrics = [
    { key: 'cac', label: 'CAC Absorbed', value: fmtUSD4(o.cac_absorbed_usd), icon: Megaphone, tone: 'muted', hint: 'Platform-paid (free bonuses)' },
    { key: 'voicerisk', label: 'Voice Risk', value: fmtUSD(o.voice_risk_usd), icon: PhoneOff, tone: (o.voice_risk_usd || 0) > 0 ? 'danger' : 'muted', hint: `Charge ${fmtUSD(o.voice_charge_usd)} · Cost ${fmtUSD(o.voice_cogs_usd)}` },
    { key: 'tenants', label: 'Tenants', value: o.total_tenants || 0, icon: Users, tone: 'muted' },
    { key: 'tx', label: 'AI Transactions', value: o.tx_count || 0, icon: Activity, tone: 'muted' },
  ];

  const tenantCols = [
    { accessorKey: 'tenant_name', header: 'Tenant', cell: ({ row }) => (
      <span className="font-medium gov-text-primary">{row.original.tenant_name}</span>
    ) },
    { accessorKey: 'plan_id', header: 'Plan', cell: ({ row }) => (
      <span className={`px-2 py-0.5 rounded text-[11px] font-semibold capitalize ${PLAN_TONE[row.original.plan_id] || PLAN_TONE.free}`}>{row.original.plan_id}</span>
    ) },
    { accessorKey: 'mrr_usd', header: 'MRR', cell: ({ row }) => fmtUSD(row.original.mrr_usd) },
    { accessorKey: 'total_revenue_usd', header: 'Revenue', cell: ({ row }) => fmtUSD(row.original.total_revenue_usd) },
    { accessorKey: 'cogs_total_usd', header: 'COGS', cell: ({ row }) => <span className="text-amber-400">{fmtUSD4(row.original.cogs_total_usd)}</span> },
    { accessorKey: 'gross_profit_usd', header: 'Gross Profit', cell: ({ row }) => <span className="text-emerald-400 font-medium">{fmtUSD(row.original.gross_profit_usd)}</span> },
    { accessorKey: 'margin_pct', header: 'Margin', cell: ({ row }) => <span className={`font-semibold ${marginTone(row.original.margin_pct)}`}>{row.original.margin_pct}%</span> },
    { accessorKey: 'voice_risk_usd', header: 'Voice Risk', cell: ({ row }) => row.original.voice_risk_usd > 0 ? <span className="text-rose-400">{fmtUSD(row.original.voice_risk_usd)}</span> : <span className="gov-text-muted">—</span> },
  ];

  const toolCols = [
    { accessorKey: 'action', header: 'Action', cell: ({ row }) => <span className="font-mono text-xs gov-text-primary">{row.original.action}</span> },
    { accessorKey: 'count', header: 'Calls', cell: ({ row }) => row.original.count },
    { accessorKey: 'revenue_usd', header: 'Revenue', cell: ({ row }) => fmtUSD4(row.original.revenue_usd) },
    { accessorKey: 'cogs_usd', header: 'COGS', cell: ({ row }) => <span className="text-amber-400">{fmtUSD4(row.original.cogs_usd)}</span> },
    { accessorKey: 'margin_pct', header: 'Margin', cell: ({ row }) => <span className={`font-semibold ${marginTone(row.original.margin_pct)}`}>{row.original.margin_pct}%</span> },
    { accessorKey: 'cost_basis', header: 'Basis', cell: ({ row }) => (
      <Badge variant="outline" className={`text-[10px] ${row.original.cost_basis === 'measured' ? 'border-emerald-500/40 text-emerald-400' : row.original.cost_basis === 'modeled' ? 'border-amber-500/40 text-amber-400' : 'border-slate-500/40 text-slate-400'}`}>{row.original.cost_basis}</Badge>
    ) },
  ];

  return (
    <div className="space-y-8" data-testid="economics-page">
      <PageHeader
        title="Unit Economics"
        subtitle="Real-time P&L — what we earn per tenant vs what we pay for tools"
        actions={
          <div className="flex items-center gap-2">
            <Select value={days} onValueChange={setDays}>
              <SelectTrigger className="w-[130px] h-9" data-testid="economics-range-selector"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={load} data-testid="economics-refresh-btn">
              <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
            </Button>
          </div>
        }
      />

      <MetricRow metrics={heroMetrics} cols={5} />
      <MetricRow metrics={secondaryMetrics} cols={4} />

      <p className="text-xs gov-text-muted -mt-4" data-testid="economics-cogs-note">
        COGS basis: data &amp; voice are <span className="text-emerald-400">measured</span> from provider bills; LLM is <span className="text-amber-400">modeled</span> (flips to measured once per-call token cost is instrumented).
      </p>

      <div className="space-y-3">
        <h2 className="text-base font-semibold gov-text-primary">Per-Tenant P&amp;L</h2>
        <DataTable
          columns={tenantCols}
          data={tenants}
          loading={loading}
          testId="economics-tenant-table"
          emptyTitle="No tenant activity in this window"
          emptyHint="Try a longer time range."
          pageSize={15}
        />
      </div>

      <div className="space-y-3">
        <h2 className="text-base font-semibold gov-text-primary">Tool Profitability</h2>
        <DataTable
          columns={toolCols}
          data={tools}
          loading={loading}
          testId="tool-margin-table"
          emptyTitle="No tool usage in this window"
          pageSize={15}
        />
      </div>
    </div>
  );
}
